import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, FileText, Sparkles, ChevronDown, ChevronUp,
  X, Trash2, History, Search, SlidersHorizontal, Diamond,
  Scale, Hammer, Gem, AlertCircle, CheckCircle2, Clock, Save,
  RotateCcw, ChevronRight,
} from 'lucide-react';
import api from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatINR(amount) {
  if (amount === null || amount === undefined) return '₹0';
  return '₹' + Number(amount).toLocaleString('en-IN');
}

function formatDate(iso) {
  if (!iso) return '';
  // Append 'Z' if no timezone info present so JS treats it as UTC (matches server storage)
  const normalized = /[Zz]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  const d = new Date(normalized);
  if (isNaN(d)) return '';
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getDiamondBadge(grade) {
  if (!grade) return { label: 'N/A', cls: 'bg-gray-400 text-white' };
  const g = grade.toUpperCase();
  if (g.includes('VVS')) return { label: grade, cls: 'bg-purple-600 text-white' };
  if (g.includes('IF')) return { label: grade, cls: 'bg-purple-500 text-white' };
  if (g.includes('VS')) return { label: grade, cls: 'bg-blue-600 text-white' };
  if (g.startsWith('HI') || g.startsWith('H-')) return { label: grade, cls: 'bg-emerald-600 text-white' };
  return { label: grade, cls: 'bg-gray-500 text-white' };
}

const PROCESSING_STEPS = [
  { icon: Upload, label: 'Reading file...' },
  { icon: Search, label: 'Extracting images...' },
  { icon: Sparkles, label: 'Running AI analysis...' },
  { icon: CheckCircle2, label: 'Generating insights...' },
];

const SORT_OPTIONS = [
  { value: 'original', label: 'Original Order' },
  { value: 'amount_desc', label: 'Total ₹ — High to Low' },
  { value: 'amount_asc', label: 'Total ₹ — Low to High' },
  { value: 'carats_desc', label: 'Diamond Carats — Heavy First' },
  { value: 'labour_pct_desc', label: 'Labour % — High to Low' },
  { value: 'weight_desc', label: 'Net Weight — Heavy First' },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

function CostBar({ metal, diamond, stone, labour, other, remaining }) {
  return (
    <div className="space-y-1">
      <div className="flex h-2.5 rounded-full overflow-hidden gap-px">
        <div style={{ width: `${metal}%` }} className="bg-amber-500" title={`Metal ${metal}%`} />
        <div style={{ width: `${diamond}%` }} className="bg-blue-500" title={`Diamond ${diamond}%`} />
        {stone > 0 && <div style={{ width: `${stone}%` }} className="bg-rose-400" title={`Stone ${stone}%`} />}
        <div style={{ width: `${labour}%` }} className="bg-emerald-500" title={`Labour ${labour}%`} />
        {other > 0 && <div style={{ width: `${other}%` }} className="bg-gray-300" title={`Other ${other}%`} />}
        {remaining > 0 && <div style={{ width: `${remaining}%` }} className="bg-orange-400" title={`Charges ${remaining}%`} />}
      </div>
      <div className="flex gap-3 text-[10px] text-gray-500 flex-wrap">
        <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1 align-middle" />Metal {metal}%</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1 align-middle" />Diamond {diamond}%</span>
        {stone > 0 && <span><span className="inline-block w-2 h-2 rounded-full bg-rose-400 mr-1 align-middle" />Stone {stone}%</span>}
        <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1 align-middle" />Labour {labour}%</span>
        {other > 0 && <span><span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-1 align-middle" />Other {other}%</span>}
        {remaining > 0 && <span><span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1 align-middle" />Charges {remaining}%</span>}
      </div>
    </div>
  );
}

function ItemCard({ item, index }) {
  const [expanded, setExpanded] = useState(false);
  const badge = getDiamondBadge(item.diamond_grade);
  const isHighLabour = (item.labour_pct || 0) > 30;
  const isPremiumDiamond = (item.diamond_grade || '').toUpperCase().includes('VVS');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden" data-testid={`item-card-${index}`}>
      {/* Hero Image */}
      <div className="relative w-full bg-gradient-to-br from-stone-800 to-stone-900" style={{ height: 200 }}>
        {item.image_base64 ? (
          <img
            src={`data:image/png;base64,${item.image_base64}`}
            alt={`${item.design || item.item_no}`}
            className="w-full h-full object-contain p-4"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <Gem className="w-12 h-12 text-amber-400 opacity-60" />
            <span className="text-stone-400 text-sm font-medium">{item.design || item.item_no || 'No Image'}</span>
          </div>
        )}
        {/* Flags */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
          {isPremiumDiamond && (
            <span className="bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Premium</span>
          )}
          {isHighLabour && (
            <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">High Labour</span>
          )}
        </div>
        {/* Sr number */}
        <div className="absolute top-2 left-2 bg-black/50 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          #{item.sr_no || index + 1}
        </div>
      </div>

      {/* Details */}
      <div className="p-4 space-y-3">
        {/* Title row */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-bold text-stone-900 text-base leading-tight">{item.design || '—'}</p>
            <p className="text-stone-400 text-xs">{item.item_no} · {item.metal}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-bold text-stone-900 text-base">{formatINR(item.total_amount)}</p>
            <p className="text-stone-400 text-xs">Total</p>
          </div>
        </div>

        {/* Data warning banner */}
        {item.data_warning && (
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-xl p-2.5 text-xs text-orange-700" data-testid={`item-warning-${index}`}>
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-orange-500" />
            <span>{item.data_warning}</span>
          </div>
        )}

        {/* Diamond row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>
            {badge.label}
          </span>
          <span className="text-stone-600 text-sm">
            <Diamond className="inline w-3.5 h-3.5 text-blue-400 mr-1" />
            {item.diamond_carats || 0}ct · {item.diamond_pcs || 0}pcs
          </span>
          {/* Show rate only for single-lot items; multi-lot shows size count */}
          {(item.diamond_lots || []).length > 1 ? (
            <span className="text-blue-500 text-xs ml-auto font-medium">{item.diamond_lots.length} size lots</span>
          ) : (item.diamond_lots || []).length === 1 && item.diamond_lots[0].rate > 0 ? (
            <span className="text-stone-500 text-xs ml-auto">{formatINR(item.diamond_lots[0].rate)}/ct</span>
          ) : null}
        </div>

        {/* Weights row */}
        <div className="flex gap-4 text-sm text-stone-600">
          <span><Scale className="inline w-3.5 h-3.5 mr-1 text-amber-500" />Gross {item.gross_weight || 0}g</span>
          <span>Net {item.net_weight || 0}g</span>
        </div>

        {/* Cost breakdown bar */}
        <CostBar
          metal={item.metal_pct || 0}
          diamond={item.diamond_pct || 0}
          stone={item.stone_pct || 0}
          labour={item.labour_pct || 0}
          other={item.other_pct || 0}
          remaining={item.remaining_pct || 0}
        />

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 text-xs text-stone-400 pt-1"
          data-testid={`item-expand-${index}`}
        >
          {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Less</> : <><ChevronDown className="w-3.5 h-3.5" /> More details</>}
        </button>

        {/* Expanded details */}
        {expanded && (
          <div className="pt-2 border-t border-stone-100 space-y-3 text-sm">

            {/* ── Summary cards ─────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-xs text-stone-400 mb-0.5">Metal</p>
                <p className="font-semibold text-amber-800">{formatINR(item.metal_amount)}</p>
                {item.metal_rate > 0 && (
                  <p className="text-[10px] text-stone-400 mt-0.5">{item.net_weight}g × ₹{item.metal_rate?.toLocaleString('en-IN')}/g</p>
                )}
              </div>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-stone-400 mb-0.5">Diamond</p>
                <p className="font-semibold text-blue-800">{formatINR(item.diamond_amount)}</p>
                <p className="text-[10px] text-stone-400 mt-0.5">{item.diamond_carats}ct · {item.diamond_pcs}pcs</p>
              </div>
              {(item.stone_amount || 0) > 0 && (
                <div className="bg-rose-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-0.5">Stone</p>
                  <p className="font-semibold text-rose-800">{formatINR(item.stone_amount)}</p>
                  {item.stone_pcs > 0 && <p className="text-[10px] text-stone-400 mt-0.5">{item.stone_pcs}pcs · {item.stone_weight}ct</p>}
                </div>
              )}
              <div className="bg-emerald-50 rounded-xl p-3">
                <p className="text-xs text-stone-400 mb-0.5">Labour</p>
                <p className="font-semibold text-emerald-800">{formatINR(item.labour_amount)}</p>
                {isHighLabour && <p className="text-[10px] text-orange-500 font-medium mt-0.5">↑ Above avg</p>}
              </div>
              {(item.other_amount || 0) > 0 && (
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-0.5">Other</p>
                  <p className="font-semibold text-stone-800">{formatINR(item.other_amount)}</p>
                </div>
              )}
              {item.remaining_amount > 0 && (
                <div className="col-span-2 bg-orange-50 rounded-xl p-3 border border-orange-100">
                  <p className="text-xs text-stone-400 mb-1">Unitemised Charges</p>
                  <p className="font-semibold text-orange-800">{formatINR(item.remaining_amount)}</p>
                  <p className="text-[10px] text-orange-500 mt-0.5">{item.remaining_pct}% of total</p>
                </div>
              )}
            </div>

            {/* ── Diamond Lots ───────────────────────────────────── */}
            {(item.diamond_lots || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Diamond Lots</p>
                <div className="bg-blue-50 rounded-xl overflow-hidden border border-blue-100">
                  <div className="grid grid-cols-4 gap-1 px-3 py-1.5 bg-blue-100">
                    <span className="text-[10px] font-bold text-stone-500">Grade · Size</span>
                    <span className="text-[10px] font-bold text-stone-500 text-center">Pcs/Ct</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Rate/ct</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Amount</span>
                  </div>
                  {item.diamond_lots.map((lot, i) => (
                    <div key={i} className="grid grid-cols-4 gap-1 px-3 py-2 border-t border-blue-100">
                      <div>
                        <p className="text-[11px] font-semibold text-stone-700">{lot.grade || '—'}</p>
                        <p className="text-[10px] text-stone-400">{lot.size || '—'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[11px] text-stone-600">{lot.pcs}p</p>
                        <p className="text-[10px] text-stone-400">{lot.carats}ct</p>
                      </div>
                      <p className="text-[11px] text-stone-600 text-right self-center">{formatINR(lot.rate)}</p>
                      <p className="text-[11px] font-semibold text-blue-800 text-right self-center">{formatINR(lot.amount)}</p>
                    </div>
                  ))}
                  {item.diamond_lots.length > 1 && (
                    <div className="grid grid-cols-4 gap-1 px-3 py-2 border-t border-blue-200 bg-blue-100/50">
                      <span className="text-[11px] font-bold text-stone-700">Total</span>
                      <div className="text-center">
                        <p className="text-[11px] font-bold text-stone-700">{item.diamond_pcs}p</p>
                        <p className="text-[10px] text-stone-500">{item.diamond_carats}ct</p>
                      </div>
                      <span></span>
                      <span className="text-[11px] font-bold text-blue-800 text-right">{formatINR(item.diamond_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Stone Lots ─────────────────────────────────────── */}
            {(item.stone_lots || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Stone Lots</p>
                <div className="bg-rose-50 rounded-xl overflow-hidden border border-rose-100">
                  <div className="grid grid-cols-4 gap-1 px-3 py-1.5 bg-rose-100">
                    <span className="text-[10px] font-bold text-stone-500">Code · Type</span>
                    <span className="text-[10px] font-bold text-stone-500 text-center">Pcs/Wt</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Rate</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Amount</span>
                  </div>
                  {item.stone_lots.map((lot, i) => (
                    <div key={i} className="grid grid-cols-4 gap-1 px-3 py-2 border-t border-rose-100">
                      <div>
                        <p className="text-[11px] font-semibold text-stone-700">{lot.code || '—'}</p>
                        <p className="text-[10px] text-stone-400">{lot.description || ''}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[11px] text-stone-600">{lot.pcs}p</p>
                        <p className="text-[10px] text-stone-400">{lot.weight}ct</p>
                      </div>
                      <p className="text-[11px] text-stone-600 text-right self-center">{lot.rate > 0 ? formatINR(lot.rate) : '—'}</p>
                      <p className="text-[11px] font-semibold text-rose-800 text-right self-center">{formatINR(lot.amount)}</p>
                    </div>
                  ))}
                  {item.stone_lots.length > 1 && (
                    <div className="grid grid-cols-4 gap-1 px-3 py-2 border-t border-rose-200 bg-rose-100/50">
                      <span className="text-[11px] font-bold text-stone-700">Total</span>
                      <div className="text-center">
                        <p className="text-[11px] font-bold text-stone-700">{item.stone_pcs}p</p>
                        <p className="text-[10px] text-stone-500">{item.stone_weight}ct</p>
                      </div>
                      <span></span>
                      <span className="text-[11px] font-bold text-rose-800 text-right">{formatINR(item.stone_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Labour Lots ────────────────────────────────────── */}
            {(item.labour_lots || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Labour / Making</p>
                <div className="bg-emerald-50 rounded-xl overflow-hidden border border-emerald-100">
                  <div className="grid grid-cols-4 gap-1 px-3 py-1.5 bg-emerald-100">
                    <span className="text-[10px] font-bold text-stone-500 col-span-2">Description</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Rate</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Amount</span>
                  </div>
                  {item.labour_lots.map((lot, i) => (
                    <div key={i} className="grid grid-cols-4 gap-1 px-3 py-2 border-t border-emerald-100">
                      <div className="col-span-2">
                        <p className="text-[11px] font-semibold text-stone-700">{lot.description || 'Making Charges'}</p>
                        {lot.type === 'per_gram' && lot.weight > 0 && (
                          <p className="text-[10px] text-stone-400">{lot.weight}g</p>
                        )}
                      </div>
                      <p className="text-[11px] text-stone-600 text-right self-center">
                        {lot.rate > 0 ? (lot.type === 'per_gram' ? `₹${lot.rate?.toLocaleString('en-IN')}/g` : formatINR(lot.rate)) : 'Lump sum'}
                      </p>
                      <p className="text-[11px] font-semibold text-emerald-800 text-right self-center">{formatINR(lot.amount)}</p>
                    </div>
                  ))}
                  {item.labour_lots.length > 1 && (
                    <div className="grid grid-cols-4 gap-1 px-3 py-2 border-t border-emerald-200 bg-emerald-100/50">
                      <span className="text-[11px] font-bold text-stone-700 col-span-3">Total</span>
                      <span className="text-[11px] font-bold text-emerald-800 text-right">{formatINR(item.labour_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Other Lots ─────────────────────────────────────── */}
            {(item.other_lots || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Other Charges</p>
                <div className="bg-stone-50 rounded-xl overflow-hidden border border-stone-200">
                  <div className="grid grid-cols-3 gap-1 px-3 py-1.5 bg-stone-200/50">
                    <span className="text-[10px] font-bold text-stone-500 col-span-2">Description</span>
                    <span className="text-[10px] font-bold text-stone-500 text-right">Amount</span>
                  </div>
                  {item.other_lots.map((lot, i) => (
                    <div key={i} className="grid grid-cols-3 gap-1 px-3 py-2 border-t border-stone-200">
                      <p className="text-[11px] font-semibold text-stone-700 col-span-2">{lot.description || 'Other'}</p>
                      <p className="text-[11px] font-semibold text-stone-700 text-right">{formatINR(lot.amount)}</p>
                    </div>
                  ))}
                  {item.other_lots.length > 1 && (
                    <div className="grid grid-cols-3 gap-1 px-3 py-2 border-t border-stone-300 bg-stone-200/40">
                      <span className="text-[11px] font-bold text-stone-700 col-span-2">Total</span>
                      <span className="text-[11px] font-bold text-stone-700 text-right">{formatINR(item.other_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── HUID ───────────────────────────────────────────── */}
            {item.huid && (
              <p className="text-stone-500 text-xs"><span className="font-medium">HUID:</span> {item.huid}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryBar({ summary }) {
  const stats = [
    { label: 'Items', value: summary.total_items || 0 },
    { label: 'Total Value', value: formatINR(summary.total_value) },
    { label: 'Gold', value: `${summary.total_gold_weight || 0}g` },
    { label: 'Diamonds', value: `${summary.total_diamond_carats || 0}ct` },
    { label: 'Avg Item', value: formatINR(summary.avg_item_value) },
  ];

  // Aggregate distribution across all items
  const total = summary.total_value || 1;
  const metalPct   = Math.round((summary.total_metal_amount   || 0) / total * 1000) / 10;
  const diamondPct = Math.round((summary.total_diamond_amount || 0) / total * 1000) / 10;
  const stonePct   = Math.round((summary.total_stone_amount   || 0) / total * 1000) / 10;
  const labourPct  = Math.round((summary.total_labour_amount  || 0) / total * 1000) / 10;
  const otherPct   = Math.round((summary.total_other_amount   || 0) / total * 1000) / 10;

  return (
    <div className="space-y-3">
      {/* Stat cards */}
      <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar px-4">
        {stats.map((s, i) => (
          <div key={i} className="shrink-0 bg-white rounded-xl px-3 py-2.5 border border-stone-200 min-w-[80px] text-center shadow-sm">
            <p className="font-bold text-stone-900 text-sm whitespace-nowrap">{s.value}</p>
            <p className="text-stone-400 text-[10px] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Overall cost distribution bar */}
      <div className="mx-4 bg-white rounded-xl px-4 py-3 border border-stone-200 shadow-sm space-y-2">
        <p className="text-[10px] text-stone-400 font-medium uppercase tracking-wide">Order Cost Distribution</p>
        <div className="flex h-3 rounded-full overflow-hidden gap-px">
          <div style={{ width: `${metalPct}%` }}   className="bg-amber-500"  title={`Metal ${metalPct}%`} />
          <div style={{ width: `${diamondPct}%` }} className="bg-blue-500"   title={`Diamond ${diamondPct}%`} />
          {stonePct > 0  && <div style={{ width: `${stonePct}%` }}  className="bg-rose-400"   title={`Stone ${stonePct}%`} />}
          <div style={{ width: `${labourPct}%` }}  className="bg-emerald-500" title={`Labour ${labourPct}%`} />
          {otherPct > 0  && <div style={{ width: `${otherPct}%` }}  className="bg-gray-300"   title={`Other ${otherPct}%`} />}
        </div>
        <div className="flex gap-3 text-[10px] text-gray-500 flex-wrap">
          <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1 align-middle" />Metal {metalPct}%</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1 align-middle" />Diamond {diamondPct}%</span>
          {stonePct > 0  && <span><span className="inline-block w-2 h-2 rounded-full bg-rose-400 mr-1 align-middle" />Stone {stonePct}%</span>}
          <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1 align-middle" />Labour {labourPct}%</span>
          {otherPct > 0  && <span><span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-1 align-middle" />Other {otherPct}%</span>}
        </div>
      </div>
    </div>
  );
}

function InsightStrip({ insights }) {
  if (!insights || insights.length === 0) return null;
  return (
    <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar px-4">
      {insights.map((insight, i) => (
        <div key={i} className="shrink-0 bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-3 max-w-[260px]">
          <div className="flex gap-2">
            <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-stone-700 text-xs leading-relaxed">{insight}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SupplierQuoteAnalyser() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // View state
  const [activeTab, setActiveTab] = useState('analyse');
  const [viewState, setViewState] = useState('upload'); // upload | processing | report
  const [processingStep, setProcessingStep] = useState(0);

  // Analysis data
  const [analysisData, setAnalysisData] = useState(null);
  const [savedQuoteId, setSavedQuoteId] = useState(null);

  // History
  const [historyList, setHistoryList] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryQuote, setSelectedHistoryQuote] = useState(null);

  // Sort / filter
  const [sortBy, setSortBy] = useState('original');
  const [filterGrade, setFilterGrade] = useState('');
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // Save sheet
  const [showSaveSheet, setShowSaveSheet] = useState(false);
  const [supplierNameInput, setSupplierNameInput] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Derived items ────────────────────────────────────────────────────────────

  const displayItems = React.useMemo(() => {
    const source = selectedHistoryQuote ? selectedHistoryQuote.items : analysisData?.items;
    if (!source) return [];

    let items = [...source];

    // Filter
    if (filterGrade) {
      items = items.filter(item => {
        const g = (item.diamond_grade || '').toUpperCase();
        if (filterGrade === 'FG-VVS') return g.includes('VVS');
        if (filterGrade === 'FG-VS') return g.includes('VS') && !g.includes('VVS');
        if (filterGrade === 'HI') return g.startsWith('HI') || g.startsWith('H-');
        if (filterGrade === 'has_image') return !!item.image_base64;
        return true;
      });
    }

    // Sort
    switch (sortBy) {
      case 'amount_desc': items.sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0)); break;
      case 'amount_asc': items.sort((a, b) => (a.total_amount || 0) - (b.total_amount || 0)); break;
      case 'carats_desc': items.sort((a, b) => (b.diamond_carats || 0) - (a.diamond_carats || 0)); break;
      case 'labour_pct_desc': items.sort((a, b) => (b.labour_pct || 0) - (a.labour_pct || 0)); break;
      case 'weight_desc': items.sort((a, b) => (b.net_weight || 0) - (a.net_weight || 0)); break;
      default: break;
    }

    return items;
  }, [analysisData, selectedHistoryQuote, sortBy, filterGrade]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    setViewState('processing');
    setProcessingStep(0);
    setSavedQuoteId(null);

    // Animate steps to give visual feedback during polling
    const stepTimers = [
      setTimeout(() => setProcessingStep(1), 1000),
      setTimeout(() => setProcessingStep(2), 2500),
    ];

    try {
      // Step 1: Upload file and get job_id immediately
      const formData = new FormData();
      formData.append('file', file);
      const uploadResp = await api.post('/supplier-quotes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000, // Only 30s needed now — job starts immediately
      });

      const { job_id } = uploadResp.data;
      setProcessingStep(2);

      // Step 2: Poll for result
      const startTime = Date.now();
      const MAX_WAIT = 180000; // 3 minute max
      const POLL_INTERVAL = 3000;

      const result = await new Promise((resolve, reject) => {
        const poll = async () => {
          if (Date.now() - startTime > MAX_WAIT) {
            return reject(new Error('Analysis timed out. Please try again.'));
          }
          try {
            const statusResp = await api.get(`/supplier-quotes/jobs/${job_id}`);
            const { status, result: jobResult, error } = statusResp.data;
            if (status === 'done') {
              resolve(jobResult);
            } else if (status === 'failed') {
              reject(new Error(error || 'Analysis failed.'));
            } else {
              setTimeout(poll, POLL_INTERVAL);
            }
          } catch (e) {
            reject(e);
          }
        };
        setTimeout(poll, POLL_INTERVAL);
      });

      stepTimers.forEach(clearTimeout);
      setProcessingStep(3);
      await new Promise(r => setTimeout(r, 600)); // brief pause for final step animation

      setAnalysisData(result);
      setSupplierNameInput(result.supplier_name || '');
      setSelectedHistoryQuote(null);
      setSortBy('original');
      setFilterGrade('');
      setViewState('report');
    } catch (err) {
      stepTimers.forEach(clearTimeout);
      setViewState('upload');
      toast.error(err.response?.data?.detail || err.message || 'Analysis failed. Please try again.');
    }
  }, []);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleSaveQuote = async () => {
    if (!analysisData) return;
    setSaving(true);
    try {
      const payload = {
        ...analysisData,
        supplier_name: supplierNameInput.trim() || analysisData.supplier_name || 'Unknown Supplier',
      };
      const resp = await api.post('/supplier-quotes', payload);
      setSavedQuoteId(resp.data.id);
      setShowSaveSheet(false);
      toast.success('Quote saved to history!');
      setHistoryLoaded(false); // force reload
    } catch (err) {
      toast.error('Failed to save quote.');
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    if (historyLoaded) return;
    setLoadingHistory(true);
    try {
      const resp = await api.get('/supplier-quotes');
      setHistoryList(resp.data || []);
      setHistoryLoaded(true);
    } catch {
      toast.error('Failed to load history.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistoryItem = async (id) => {
    try {
      const resp = await api.get(`/supplier-quotes/${id}`);
      setSelectedHistoryQuote(resp.data);
      setSortBy('original');
      setFilterGrade('');
    } catch {
      toast.error('Failed to load quote.');
    }
  };

  const deleteHistoryItem = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/supplier-quotes/${id}`);
      setHistoryList(prev => prev.filter(q => q.id !== id));
      if (selectedHistoryQuote?.id === id) setSelectedHistoryQuote(null);
      toast.success('Quote deleted.');
    } catch {
      toast.error('Failed to delete.');
    }
  };

  const activeFilterCount = (filterGrade ? 1 : 0) + (sortBy !== 'original' ? 1 : 0);
  const currentData = selectedHistoryQuote || analysisData;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col max-w-lg mx-auto relative">

      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center text-stone-600 -ml-1"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-stone-900 text-base leading-tight truncate">Supplier Quote Analyser</h1>
            {currentData?.supplier_name && (
              <p className="text-xs text-stone-400 truncate">{currentData.supplier_name}</p>
            )}
          </div>
          {/* Actions when report is shown */}
          {viewState === 'report' && analysisData && !selectedHistoryQuote && (
            <div className="flex gap-1">
              {!savedQuoteId && (
                <button
                  onClick={() => { setShowSaveSheet(true); setSupplierNameInput(analysisData.supplier_name || ''); }}
                  className="flex items-center gap-1 bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
                  data-testid="save-quote-btn"
                >
                  <Save className="w-3.5 h-3.5" />Save
                </button>
              )}
              <button
                onClick={() => { setViewState('upload'); setAnalysisData(null); setSavedQuoteId(null); }}
                className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600"
                title="New analysis"
                data-testid="new-analysis-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
          {selectedHistoryQuote && (
            <button
              onClick={() => setSelectedHistoryQuote(null)}
              className="w-8 h-8 flex items-center justify-center text-stone-400"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tabs */}
        {!selectedHistoryQuote && (
          <div className="flex px-4 pb-0 gap-1">
            {['analyse', 'history'].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === 'history') loadHistory();
                }}
                className={`flex-1 py-2 text-sm font-semibold capitalize rounded-none border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-amber-600 text-amber-700'
                    : 'border-transparent text-stone-400'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'history' ? 'History' : 'Analyse'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}

      {/* History detail view */}
      {selectedHistoryQuote && (
        <ReportContent
          data={selectedHistoryQuote}
          items={displayItems}
          sortBy={sortBy}
          setSortBy={setSortBy}
          filterGrade={filterGrade}
          setFilterGrade={setFilterGrade}
          showFilterSheet={showFilterSheet}
          setShowFilterSheet={setShowFilterSheet}
          activeFilterCount={activeFilterCount}
        />
      )}

      {/* Analyse tab */}
      {!selectedHistoryQuote && activeTab === 'analyse' && (
        <>
          {viewState === 'upload' && (
            <UploadScreen
              fileInputRef={fileInputRef}
              onFileSelect={handleFileSelect}
              onDrop={handleFileDrop}
            />
          )}
          {viewState === 'processing' && <ProcessingScreen step={processingStep} />}
          {viewState === 'report' && analysisData && (
            <ReportContent
              data={analysisData}
              items={displayItems}
              sortBy={sortBy}
              setSortBy={setSortBy}
              filterGrade={filterGrade}
              setFilterGrade={setFilterGrade}
              showFilterSheet={showFilterSheet}
              setShowFilterSheet={setShowFilterSheet}
              activeFilterCount={activeFilterCount}
              savedQuoteId={savedQuoteId}
            />
          )}
        </>
      )}

      {/* History tab */}
      {!selectedHistoryQuote && activeTab === 'history' && (
        <HistoryView
          list={historyList}
          loading={loadingHistory}
          onOpen={openHistoryItem}
          onDelete={deleteHistoryItem}
        />
      )}

      {/* Save Bottom Sheet */}
      {showSaveSheet && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setShowSaveSheet(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-lg mx-auto bg-white rounded-t-3xl px-5 pt-5 pb-8 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-2" />
            <h2 className="font-bold text-stone-900 text-lg">Save Quote</h2>
            <div>
              <label className="text-sm text-stone-600 mb-1 block">Supplier Name</label>
              <input
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="e.g. Hallmark Jewellers Mumbai"
                value={supplierNameInput}
                onChange={e => setSupplierNameInput(e.target.value)}
                data-testid="supplier-name-input"
                autoFocus
              />
            </div>
            <button
              onClick={handleSaveQuote}
              disabled={saving}
              className="w-full bg-amber-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-60"
              data-testid="confirm-save-btn"
            >
              {saving ? 'Saving...' : 'Save to History'}
            </button>
          </div>
        </div>
      )}

      {/* Filter Bottom Sheet */}
      {showFilterSheet && (
        <FilterSheet
          sortBy={sortBy}
          setSortBy={setSortBy}
          filterGrade={filterGrade}
          setFilterGrade={setFilterGrade}
          onClose={() => setShowFilterSheet(false)}
        />
      )}
    </div>
  );
}

// ─── Upload Screen ─────────────────────────────────────────────────────────────

function UploadScreen({ fileInputRef, onFileSelect, onDrop }) {
  const [dragging, setDragging] = useState(false);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 gap-8">
      {/* Drop zone */}
      <div
        className={`w-full border-2 border-dashed rounded-3xl p-10 flex flex-col items-center gap-4 transition-colors cursor-pointer ${
          dragging ? 'border-amber-500 bg-amber-50' : 'border-stone-300 bg-white'
        }`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { setDragging(false); onDrop(e); }}
        onClick={() => fileInputRef.current?.click()}
        data-testid="upload-dropzone"
      >
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
          <Upload className="w-9 h-9 text-amber-600" />
        </div>
        <div className="text-center">
          <p className="font-bold text-stone-900 text-base">Tap to upload quotation</p>
          <p className="text-stone-400 text-sm mt-1">PDF, Excel (.xlsx), or CSV</p>
          <p className="text-stone-300 text-xs mt-0.5">Max 50 MB</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.xlsx,.xls,.csv"
          className="hidden"
          onChange={e => onFileSelect(e.target.files?.[0])}
          data-testid="file-input"
        />
      </div>

      {/* What you'll get */}
      <div className="w-full bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <p className="text-xs font-bold text-stone-400 uppercase tracking-wider px-4 pt-4 pb-2">What you get</p>
        {[
          { icon: Sparkles, text: 'AI-powered cost breakdown analysis' },
          { icon: Diamond, text: 'Diamond quality distribution & flags' },
          { icon: Hammer, text: 'Labour negotiation opportunities' },
          { icon: Gem, text: 'Product images extracted from PDF' },
        ].map(({ icon: Icon, text }, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-stone-100 first:border-0">
            <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-amber-600" />
            </div>
            <p className="text-stone-700 text-sm">{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Processing Screen ─────────────────────────────────────────────────────────

function ProcessingScreen({ step }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 gap-10">
      {/* Animated gem */}
      <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-xl animate-pulse">
        <Gem className="w-12 h-12 text-white" />
      </div>

      <div className="w-full space-y-4">
        {PROCESSING_STEPS.map(({ icon: Icon, label }, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div key={i} className={`flex items-center gap-4 transition-opacity ${i > step ? 'opacity-30' : 'opacity-100'}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                done ? 'bg-emerald-500' : active ? 'bg-amber-500 animate-pulse' : 'bg-stone-200'
              }`}>
                <Icon className={`w-4 h-4 ${done || active ? 'text-white' : 'text-stone-400'}`} />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${done ? 'text-emerald-600' : active ? 'text-amber-700' : 'text-stone-400'}`}>
                  {label}
                </p>
                {active && (
                  <div className="mt-1 h-1 bg-stone-200 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full animate-[progress_2s_ease-in-out_infinite]" style={{ width: '60%' }} />
                  </div>
                )}
              </div>
              {done && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
            </div>
          );
        })}
      </div>

      <p className="text-stone-400 text-sm text-center">
        Gemini is reading your quotation...<br />
        <span className="text-xs">This takes about 15–30 seconds</span>
      </p>
    </div>
  );
}

// ─── Report Content ────────────────────────────────────────────────────────────

function ReportContent({ data, items, sortBy, setSortBy, filterGrade, setFilterGrade, showFilterSheet, setShowFilterSheet, activeFilterCount }) {
  return (
    <div className="flex-1 flex flex-col overflow-y-auto pb-8">
      {/* AI Insights */}
      {data.ai_insights?.length > 0 && (
        <div className="pt-4 pb-2">
          <p className="px-4 text-xs font-bold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-amber-500" />AI Insights
          </p>
          <InsightStrip insights={data.ai_insights} />
        </div>
      )}

      {/* Summary */}
      {data.summary && Object.keys(data.summary).length > 0 && (
        <div className="py-3">
          <SummaryBar summary={data.summary} />
        </div>
      )}

      {/* Sort/Filter bar */}
      <div className="flex items-center justify-between px-4 py-2">
        <p className="text-sm text-stone-500">
          <span className="font-semibold text-stone-800">{items.length}</span> item{items.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={() => setShowFilterSheet(true)}
          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
            activeFilterCount > 0
              ? 'bg-amber-600 text-white border-amber-600'
              : 'bg-white text-stone-600 border-stone-200'
          }`}
          data-testid="filter-sort-btn"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Sort & Filter
          {activeFilterCount > 0 && (
            <span className="bg-white text-amber-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Item cards */}
      <div className="px-4 space-y-4">
        {items.length === 0 ? (
          <div className="text-center py-12 text-stone-400">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">
              {activeFilterCount > 0
                ? 'No items match your filters'
                : 'No items found in this document'}
            </p>
          </div>
        ) : (
          items.map((item, i) => <ItemCard key={item.item_no || i} item={item} index={i} />)
        )}
      </div>
    </div>
  );
}

// ─── History View ──────────────────────────────────────────────────────────────

function HistoryView({ list, loading, onOpen, onDelete }) {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <Clock className="w-8 h-8 text-stone-300 mx-auto animate-spin" />
          <p className="text-stone-400 text-sm">Loading history...</p>
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center space-y-3">
          <History className="w-12 h-12 text-stone-200 mx-auto" />
          <p className="font-semibold text-stone-400">No saved quotes yet</p>
          <p className="text-stone-300 text-sm">Analyse a quotation and tap "Save"</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
      {list.map(q => (
        <div
          key={q.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(q.id)}
          onKeyDown={(e) => e.key === 'Enter' && onOpen(q.id)}
          className="w-full bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-3 text-left shadow-sm active:bg-stone-50 cursor-pointer"
          data-testid={`history-item-${q.id}`}
        >
          <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center shrink-0">
            <FileText className="w-6 h-6 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-stone-900 text-sm truncate">{q.supplier_name || 'Unknown Supplier'}</p>
            <p className="text-stone-400 text-xs mt-0.5">{q.summary?.total_items || 0} items · {formatINR(q.summary?.total_value)}</p>
            <p className="text-stone-300 text-xs mt-0.5">{formatDate(q.created_at)}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={(e) => onDelete(q.id, e)}
              className="w-8 h-8 flex items-center justify-center text-stone-300 hover:text-red-400 rounded-full"
              data-testid={`delete-quote-${q.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <ChevronRight className="w-4 h-4 text-stone-300" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Filter Sheet ──────────────────────────────────────────────────────────────

function FilterSheet({ sortBy, setSortBy, filterGrade, setFilterGrade, onClose }) {
  const grades = [
    { value: '', label: 'All Grades' },
    { value: 'FG-VVS', label: 'FG-VVS (Premium)' },
    { value: 'FG-VS', label: 'FG-VS (Good)' },
    { value: 'HI', label: 'HI Grade' },
    { value: 'has_image', label: 'Has Product Image' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-lg mx-auto bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-1" />
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-stone-900 text-lg">Sort & Filter</h2>
          <button onClick={onClose} className="text-stone-400"><X className="w-5 h-5" /></button>
        </div>

        {/* Sort */}
        <div>
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Sort By</p>
          <div className="space-y-1">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-colors ${
                  sortBy === opt.value
                    ? 'bg-amber-600 text-white font-semibold'
                    : 'bg-stone-50 text-stone-700 hover:bg-stone-100'
                }`}
                data-testid={`sort-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filter by grade */}
        <div>
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Diamond Grade</p>
          <div className="flex gap-2 flex-wrap">
            {grades.map(g => (
              <button
                key={g.value}
                onClick={() => setFilterGrade(g.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  filterGrade === g.value
                    ? 'bg-amber-600 text-white'
                    : 'bg-stone-100 text-stone-600'
                }`}
                data-testid={`filter-grade-${g.value || 'all'}`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Reset */}
        {(sortBy !== 'original' || filterGrade) && (
          <button
            onClick={() => { setSortBy('original'); setFilterGrade(''); onClose(); }}
            className="w-full py-3 border border-stone-200 rounded-xl text-sm text-stone-500 font-medium"
            data-testid="reset-filters-btn"
          >
            Reset All
          </button>
        )}
      </div>
    </div>
  );
}
